import React, { useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBookmark } from "@fortawesome/free-regular-svg-icons";
import { faBookmark as faBookmarkSolid } from "@fortawesome/free-solid-svg-icons";
import { mapGender, copyToClipboard, renderCopyIcon } from "../utils/utils.js";

const host = process.env.REACT_APP_SERVER;

function UserTable({ users, projectId, onBookmarkToggle, isCurrentList }) {
  console.log("isCurrentList", isCurrentList);
  const [loading, setLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [selectAll, setSelectAll] = useState(false);

  users = users.map((obj) => {
    return { ...obj, ["age"]: new Date().getFullYear() - obj.year_of_birth };
  });

  const compareValues = (valueA, valueB) => {
    // console.log(valueA, valueB, typeof valueA, sortDirection);

    const getComparableValue = (value) => {
      // If value is an array, return the first element
      if (Array.isArray(value)) {
        return value[0];
      }
      // Otherwise, return the value itself
      return value;
    };

    const comparableValueA = getComparableValue(valueA);
    const comparableValueB = getComparableValue(valueB);

    // Check if both values are strings
    if (typeof comparableValueA === "string" && typeof comparableValueB === "string") {
      // Alphabetical sorting for strings
      return sortDirection === "asc" ? comparableValueA.localeCompare(comparableValueB) : comparableValueB.localeCompare(comparableValueA);
    }

    // Check if both values are numbers
    if (typeof comparableValueA === "number" && typeof comparableValueB === "number") {
      // Numerical sorting for numbers
      return sortDirection === "asc" ? comparableValueA - comparableValueB : comparableValueB - comparableValueA;
    }

    // Fallback for other cases
    return 0; // Consider them equal and maintain their relative positions
  };

  const sortedNameData = [...users].sort((a, b) => {
    // console.log("sortedNameData", sortColumn, sortDirection);
    if (sortColumn) {
      const valueA = a[sortColumn];
      const valueB = b[sortColumn];
      return compareValues(valueA, valueB);
    }
    return 0;
  });

  const handleSort = (column) => {
    // console.log("handleSort", column);
    // console.log("sortColumn", sortColumn, "sortDirection", sortDirection);

    if (sortColumn === column) {
      const newSortDirection = sortDirection === "asc" ? "desc" : "asc";
      // console.log("New sort direction:", newSortDirection);
      setSortDirection(newSortDirection);
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const handleSelectAll = () => {
    const updatedUsers = users.map((user) => ({ ...user, bookmarked: !selectAll }));
    handleBookmarkToggleBatch(updatedUsers, !selectAll);
    setSelectAll(!selectAll);
  };

  const handleBookmarkToggle = async (user, bookmark = !user.bookmarked) => {
    const updatedUser = { ...user, bookmarked: bookmark };
    try {
      const response = await fetch(`${host}/api/projects/${projectId}/users/bookmark`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userIds: [user.id], isBookmarked: bookmark }),
      });

      if (response.ok) {
        onBookmarkToggle([updatedUser]);
      } else {
        console.error("Failed to update bookmark status");
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleBookmarkToggleBatch = async (users, bookmark) => {
    try {
      const userIds = users.map((user) => user.id);
      const updatedUsers = users.map((user) => ({ ...user, bookmarked: bookmark }));

      const response = await fetch(`${host}/api/projects/${projectId}/users/bookmark`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userIds, isBookmarked: bookmark }),
      });

      if (response.ok) {
        onBookmarkToggle(updatedUsers, bookmark);
      } else {
        console.error("Failed to update bookmark status");
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  return (
    <div className="table-container">
      <div className="flex items-center mb-2">
        {isCurrentList ? (
          <></>
        ) : (
          <button className="btn btn-sm btn-outline btn-secondary" onClick={handleSelectAll}>
            {selectAll ? "Unbookmark All" : "Bookmark All"}
          </button>
        )}
      </div>
      <table className="table table-xs table-zebra">
        <thead>
          <tr>
            <th className="sticky top-0 px-2 py-1 cursor-pointer" onClick={() => handleSort("bookmarked")}>
              Bookmarked {sortColumn === "bookmarked" && (sortDirection === "asc" ? "↑" : "↓")}
            </th>
            <th className="sticky top-0 px-2 py-1 cursor-pointer" onClick={() => handleSort("name")}>
              Name {sortColumn === "name" && (sortDirection === "asc" ? "↑" : "↓")}
            </th>
            <th className="sticky top-0 px-2 py-1 cursor-pointer" onClick={() => handleSort("gender")}>
              Gender {sortColumn === "gender" && (sortDirection === "asc" ? "↑" : "↓")}
            </th>
            <th className="sticky top-0 px-2 py-1 cursor-pointer" onClick={() => handleSort("ethnicity")}>
              Ethnicity {sortColumn === "ethnicity" && (sortDirection === "asc" ? "↑" : "↓")}
            </th>
            <th className="sticky top-0 px-2 py-1 cursor-pointer" onClick={() => handleSort("age")}>
              Age {sortColumn === "age" && (sortDirection === "asc" ? "↑" : "↓")}
            </th>
            <th className="sticky top-0 px-2 py-1 cursor-pointer" onClick={() => handleSort("occupation")}>
              Occupation {sortColumn === "occupation" && (sortDirection === "asc" ? "↑" : "↓")}
            </th>
            <th className="sticky top-0 px-2 py-1 cursor-pointer" onClick={() => handleSort("household_income")}>
              Income {sortColumn === "household_income" && (sortDirection === "asc" ? "↑" : "↓")}
            </th>
            <th className="sticky top-0 px-2 py-1 cursor-pointer" onClick={() => handleSort("phone_number_self_described")}>
              Phone Number {sortColumn === "phone_number_self_described" && (sortDirection === "asc" ? "↑" : "↓")}
            </th>
            <th className="sticky top-0 px-2 py-1 cursor-pointer" onClick={() => handleSort("email_address")}>
              Email {sortColumn === "email_address" && (sortDirection === "asc" ? "↑" : "↓")}
            </th>
            <th className="sticky top-0 px-2 py-1 cursor-pointer" onClick={() => handleSort("market")}>
              Market {sortColumn === "market" && (sortDirection === "asc" ? "↑" : "↓")}
            </th>
            <th className="sticky top-0 px-2 py-1 cursor-pointer" onClick={() => handleSort("instagram")}>
              Instagram {sortColumn === "instagram" && (sortDirection === "asc" ? "↑" : "↓")}
            </th>
            <th className="sticky top-0 px-2 py-1 cursor-pointer" onClick={() => handleSort("linkedin")}>
              Linkedin {sortColumn === "linkedin" && (sortDirection === "asc" ? "↑" : "↓")}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedNameData.map((user) => (
            <tr key={user.id}>
              <td className="border px-2 py-1 truncate">
                <FontAwesomeIcon icon={user.bookmarked ? faBookmarkSolid : faBookmark} size="1x" className={user.bookmarked ? "primary" : "null"} onClick={() => handleBookmarkToggle(user)} />{" "}
              </td>
              <td className="border px-2 py-1 truncate">
                <a href={`/project/${projectId}/profile/${user.id}`} target="_blank" rel="noopener noreferrer" className="text-blue-500">
                  {user.name}
                </a>
              </td>

              <td className="border px-2 py-1 truncate">{mapGender(user.gender)}</td>
              <td className="border px-2 py-1 truncate">{user.ethnicity.join(" & ")}</td>
              <td className="border px-2 py-1 truncate">{user.age}</td>
              <td className="border px-2 py-1 truncate">{user.occupation ? user.occupation : user.occupation_self_described}</td>
              <td className="border px-2 py-1 truncate">
                {user.household_income
                  ? `$${Number(user.household_income)
                      .toFixed(0)
                      .toString()
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
                  : user.household_income_self_described}
              </td>
              <td className="border px-2 py-1 truncate">{user.phone_number_self_described ? user.phone_number_self_described : user.phone_number_self_described}</td>
              <td className="border px-2 py-1 truncate">{user.email_address}</td>
              <td className="border px-2 py-1 truncate">{user.market}</td>
              <td className="border px-2 py-1 truncate">{user.instagram}</td>
              <td className="border px-2 py-1 truncate">{user.linkedin}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default UserTable;
